import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class StepperNavigationService {
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);

  // Step completion signals
  private stepsCompletionSignal: WritableSignal<boolean[]> = signal([]);
  private totalStepsSignal: WritableSignal<number> = signal(0);
  private currentStepIndexSignal: WritableSignal<number> = signal(0);

  // Readable signals
  public stepsCompletion: Signal<readonly boolean[]> = this.stepsCompletionSignal.asReadonly();
  public totalSteps: Signal<number> = this.totalStepsSignal.asReadonly();
  public currentStepIndex: Signal<number> = this.currentStepIndexSignal.asReadonly();

  // Computed signal for next step enablement
  public isNextStepEnabled = computed(() => {
    const currentStep = this.currentStepIndexSignal();
    const totalSteps = this.totalStepsSignal();
    if (totalSteps === 0 || currentStep >= totalSteps - 1) {
      return false;
    }
    return this.stepsCompletionSignal()[currentStep];
  });

  public initializeStepper(numberOfSteps: number): void {
    this.totalStepsSignal.set(numberOfSteps);
    this.stepsCompletionSignal.set(new Array(numberOfSteps).fill(false));
    this.currentStepIndexSignal.set(0);
  }

  public moveToNextStep(stepper: MatStepper): void {
    if (stepper && this.currentStepIndexSignal() < this.totalStepsSignal() - 1) {
      stepper.next();
      this.currentStepIndexSignal.set(stepper.selectedIndex);
    }
  }

  public setStepCompletion(stepIndex: number, isCompleted: boolean): void {
    if (stepIndex >= 0 && stepIndex < this.totalStepsSignal()) {
      this.stepsCompletionSignal.update(currentStatus => {
        const newStatus = [...currentStatus];
        newStatus[stepIndex] = isCompleted;
        return newStatus;
      });
    } else {
      console.warn(`Attempted to set completion for an invalid step index: ${stepIndex}`);
    }
  }

  public onStepChange(event: StepperSelectionEvent): boolean {
    const previouslySelectedIndex = event.previouslySelectedIndex;
    const currentIndex = event.selectedIndex;
    const totalSteps = this.totalStepsSignal();

    this.currentStepIndexSignal.set(currentIndex);

    if (currentIndex < previouslySelectedIndex) {
      return true;
    }

    if (currentIndex > previouslySelectedIndex) {
      if (previouslySelectedIndex >= 0 && previouslySelectedIndex < totalSteps) {
        if (!this.stepsCompletionSignal()[previouslySelectedIndex]) {
          this.showStepIncompleteError();
          return false;
        }
      }
    }

    return true;
  }

  private showStepIncompleteError(): void {
    this.notificationService.info(this.translate.instant('NOTIFICATIONS.INFO.STEP_INCOMPLETE'));
  }
}
